import { useEffect, useState, useCallback, useRef } from 'react'
import { initVersionCheck, VersionInfo, StoredVersionInfo } from '@/utils/versionCheck'

export interface UseVersionCheckResult {
  isNewVersion: boolean
  currentVersion: VersionInfo | null
  previousVersion: StoredVersionInfo | null
  isLoading: boolean
  error: Error | null
  checkVersion: () => Promise<void>
}

/**
 * Hook: 檢查應用版本是否有更新
 */
export default function useVersionCheck(): UseVersionCheckResult {
  const [isNewVersion, setIsNewVersion] = useState(false)
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null)
  const [previousVersion, setPreviousVersion] = useState<StoredVersionInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const hasCheckedRef = useRef(false)
  const isCheckingRef = useRef(false)

  const checkVersion = useCallback(async () => {
    if (isCheckingRef.current) {
      return
    }

    try {
      isCheckingRef.current = true
      setIsLoading(true)
      setError(null)
      // 讀取 version.json 檔案
      const result = await initVersionCheck()

      setIsNewVersion(result.isNewVersion)
      setCurrentVersion(result.currentVersion)
      setPreviousVersion(result.previousVersion)

      if (result.isNewVersion && result.previousVersion && result.currentVersion) {
        console.log(
          `[版本更新] 檢測到新版本: ${result.currentVersion.version} (舊版本: ${result.previousVersion.version})`
        )
      } else if (result.currentVersion) {
        console.log(`[版本資訊] 當前版本: ${result.currentVersion.version}`)
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('版本檢查失敗')
      setError(error)
      console.error('[版本檢查] 錯誤:', error)
    } finally {
      setIsLoading(false)
      isCheckingRef.current = false
    }
  }, [])

  useEffect(() => {
    // 只在首次掛載時檢查一次
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true
      checkVersion()
    }
  }, [checkVersion])

  return {
    isNewVersion,
    currentVersion,
    previousVersion,
    isLoading,
    error,
    checkVersion,
  }
}
